import ContactLink from "@/components/ContactLink";
import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import StatusBadge from "@/components/shared/StatusBadge";

export default function CampaignLifecycle() {
  return (
    <>
      <p>
        Your campaign moves through four stages on its own — based on the dates you set, no buttons to push. Knowing where you are tells you what you can change and what's frozen.
      </p>

      <h2>The four stages</h2>
      <div className="not-prose grid gap-3 sm:grid-cols-2 my-4">
        <StateCard status="draft" desc="Setting up. Hasn't started yet." />
        <StateCard status="active" desc="Reading and donations are open." />
        <StateCard status="grace" desc="Reading's done; late donations still welcome. A week, by default." />
        <StateCard status="locked" desc="Final results, frozen for keeps." />
      </div>

      <h3>Getting ready</h3>
      <p>
        Where you'll do all your setup, with the setup guide leading the way. Pick dates, add classrooms and students, invite teachers, set up rewards. A campaign stays here until its fee is paid and we've approved it, even past its start date (see <Link href="/help/articles/paying-for-your-campaign">Paying for your campaign</Link>). Once it's approved, donations can open before your start date: right away by default, or on a date you choose. That gives families time to share with grandparents. From then on the campaign shows as <strong>Donations open</strong>, and your school's donation page and every student's page are live, but students can't log reading minutes until the start date.
      </p>

      <h3>Live</h3>
      <p>
        The read-a-thon is on. Students log minutes, donations come in, rewards unlock. You can still adjust the roster, the goals, and the rewards as you go. The only things fixed at this point are the start and end dates.
      </p>

      <h3>Wrapping up</h3>
      <p>
        Reading is over, but donations keep coming in for a while — a week by default — a chance for last-minute family chip-ins. Families have until the logging deadline (9:00 AM the morning after the last day, by default) to log any final minutes, and we email them to say reading is wrapping up. The roster's frozen at this point, but you can still tweak rewards if you'd like to add a "we got close!" tier.
      </p>

      <h3>Finished</h3>
      <p>
        Final results. You can look at everything but can't change anything. We send a "final results" email so the whole school can celebrate.
      </p>

      <Callout variant="note" title="Refunds after the finish still count">
        <p>
          If a donation is refunded after the campaign finishes, it comes off the totals and the leaderboards, the same as any refund.
        </p>
      </Callout>

      <Callout variant="note" title="Late activations still work">
        <p>
          A parent who finds the take-home sheet weeks later can still activate their child. They won't be able to add minutes or donations, but they'll see how everything turned out.
        </p>
      </Callout>

      <h2>A few things to know</h2>
      <p>
        Two campaigns can't run at the same time — once one finishes, the next can start. You choose how long donations open early, how long families can log minutes and how long donations stay open when you plan the campaign. Once a campaign is live, those and the start and end dates can't be changed (if you absolutely need to, email <ContactLink>hello@pagepledge.com</ContactLink> and we'll help).
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/after-the-campaign">After the campaign ends</Link>.</li>
        <li><Link href="/help/articles/creating-a-campaign">Creating your campaign</Link>.</li>
      </ul>
    </>
  );
}

function StateCard({ status, desc }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-1.5">
      <StatusBadge status={status} showActive />
      <p className="text-sm text-muted-foreground leading-relaxed mt-1">{desc}</p>
    </div>
  );
}
